import { createContext, useContext, type ReactNode } from "react";

/**
 * Lets the shared chat rail show a concept-idea preview on a row that saved or
 * updated an idea — or its production brief — without knowing what an idea or a
 * workshop side panel is. The workshop chat supplies the card (it owns the query
 * and the panel handle); everywhere else nothing renders.
 */
export type ConceptIdeaCardRenderer = (idea: SavedConceptIdeaRef) => ReactNode;

export const ConceptIdeaCardContext = createContext<ConceptIdeaCardRenderer | null>(
  null,
);

export function useConceptIdeaCard() {
  return useContext(ConceptIdeaCardContext);
}

export const SAVE_CONCEPT_IDEA_TOOL = "SaveConceptIdea";
export const UPDATE_CONCEPT_IDEA_TOOL = "UpdateConceptIdea";
export const SAVE_CONCEPT_BRIEF_TOOL = "SaveConceptBrief";
export const UPDATE_CONCEPT_BRIEF_TOOL = "UpdateConceptBrief";

/** Every write that answers with the idea's `ideaId` and `title` at the top level. */
const CONCEPT_IDEA_WRITE_TOOLS = new Set([
  SAVE_CONCEPT_IDEA_TOOL,
  UPDATE_CONCEPT_IDEA_TOOL,
  SAVE_CONCEPT_BRIEF_TOOL,
  UPDATE_CONCEPT_BRIEF_TOOL,
]);

export type SavedConceptIdeaRef = { id: string; title: string | null };

/**
 * The ideas a finished turn saved or updated, read off its tool results — one
 * per idea, in first-touched order. Saving an idea and then writing its brief is
 * one idea to open, not two cards.
 */
export function savedConceptIdeas(
  events: { toolName: string; result?: unknown }[],
): SavedConceptIdeaRef[] {
  const byId = new Map<string, SavedConceptIdeaRef>();
  for (const event of events) {
    if (!CONCEPT_IDEA_WRITE_TOOLS.has(event.toolName)) continue;
    const result = event.result;
    if (!result || typeof result !== "object" || !("ideaId" in result)) continue;
    const id = String((result as { ideaId: unknown }).ideaId ?? "");
    if (!id) continue;
    const title =
      "title" in result ? String((result as { title: unknown }).title ?? "") : "";
    // The latest title wins; the position stays where the idea first appeared.
    byId.set(id, { id, title: title || byId.get(id)?.title || null });
  }
  return [...byId.values()];
}
